/**
 * Route guard that enforces PIN verification for mygiving sub-pages.
 *
 * The PIN overlay lives on `/my-giving` (the landing page). This guard
 * redirects users to that page if they haven't verified their PIN this
 * session, preventing deep-link or back-button bypass.
 *
 * Mobile's `pinVerifiedThisSession` is kept in sync with the server-side PIN
 * session through `getPinState` + `verifyPin`; the session ends when the member
 * goes home (see @/utils/giving-pin-session). giving-api enforces it on bank
 * linking, recurring changes and large FAST payments.
 *
 * The intended destination is preserved as a `redirect` query param so
 * the landing page can send the user there after successful PIN entry
 * (e.g. tapping a giving notification opens the transaction detail).
 *
 * Exempt routes:
 *  - /my-giving          → has the PIN overlay itself
 *  - /my-giving/pin/*    → PIN creation / forgot-PIN flow
 *  - /my-giving/bo-redirection → bank OAuth callback must complete
 */
export function createMyGivingPinGuard(store) {
  return (to, from, next) => {
    if (to.path === '/my-giving') {
      return next();
    }

    if (to.path.startsWith('/my-giving/pin/')) {
      return next();
    }

    if (to.path === '/my-giving/bo-redirection') {
      return next();
    }

    if (store.state.myGiving.pinVerifiedThisSession) {
      return next();
    }

    // Stash intended destination so the landing page can complete the nav
    // after PIN verification.
    next({ path: '/my-giving', query: { redirect: to.fullPath || to.path }, replace: true });
  };
}
